import { fabric } from 'fabric';
import EventPublisher from '../event_publisher';
import {
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  CELL_SIZE,
  TOP_PADDING
} from './render_properties';

export default class LandmarkRenderer {
  constructor(landmark, readOnly = false) {
    this.landmark = landmark;
    this.readOnly = readOnly;
    this.topPadding = readOnly ? TOP_PADDING : 0;
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    return this;
  }

  render() {
    this.renderSVG();
  }

  async renderSVG() {
    const response = await fetch(this.landmark.svgURL);
    fabric.loadSVGFromString(await response.text(), (objects, options) => {
      const obj = fabric.util.groupSVGElements(objects, options);
      this.svg = obj;
      const { width, height } = obj;
      const scaleX = (this.landmark.width * CELL_SIZE) / width;
      const scaleY = (this.landmark.height * CELL_SIZE) / height;
      this.label = this.renderLabel(this.landmark.label);
      obj.set({ scaleX: scaleX, scaleY: scaleY, originX: 'center', originY: 'center', opacity: 0.8 });
      this.renderedElement = new fabric.Group([this.label, this.svg], {
        left: this.landmark.topLeftX * CELL_SIZE,
        top: (this.landmark.topLeftY * CELL_SIZE) + this.topPadding,
        angle: this.landmark.rotation,
        lockMovementX: this.readOnly, lockMovementY: this.readOnly
      });
      this.setupEventListeners()
      this.canvas.add(this.renderedElement);
      this.canvas.sendToBack(this.renderedElement);
    });
  }

  setupEventListeners() {
    this.renderedElement.on('selected', this.onSelected);
    this.renderedElement.on('moving', this.onMoving);
    this.renderedElement.on('scaling', this.onMoving);
    this.renderedElement.on('modified', this.onModified);
    this.renderedElement.on('selection:moved', this.onSelectionMoved);
    this.renderedElement.on('selection:created', this.onSelectionCreated);
  }

  onModified = event => {
    switch (event.action) {
    case 'drag':
      this.onMoved(event)
      break
    case 'scale':
    case 'scaleX':
    case 'scaleY':
      this.onScaled(event)
      break
    case 'rotate':
      this.onRotated(event)
    }
  }

  onMoving = () => {
    const { left, top } = this.renderedElement;
    this.landmark.topLeftX = left / CELL_SIZE;
    this.landmark.topLeftY = top / CELL_SIZE;
    EventPublisher.publish('object-moving', { object: this.landmark });
  }

  onRotated = () => {
    const { left, top, angle } = this.renderedElement;
    this.landmark.topLeftX = left / CELL_SIZE;
    this.landmark.topLeftY = top / CELL_SIZE;
    this.landmark.rotation = angle;
  }

  onSelected = () => {
    EventPublisher.publish('landmark-selected', { landmark: this.landmark });
  }

  onSelectionCreated = () => {
    this.initialX = this.landmark.topLeftX;
    this.initialY = this.landmark.topLeftY;
  }

  onSelectionMoved = event => {
    const { leftOffset, topOffset } = event;
    this.landmark.topLeftX = (leftOffset / CELL_SIZE) + this.initialX;
    this.landmark.topLeftY = (topOffset / CELL_SIZE) + this.initialY;
    EventPublisher.publish('object-moved', { object: this.landmark });
  }

  onMoved = () => {
    EventPublisher.publish('object-moved', { object: this.landmark });
  }

  onScaled = () => {
    const { left, top, width, height, scaleX, scaleY } = this.renderedElement;
    this.landmark.topLeftX = left / CELL_SIZE;
    this.landmark.topLeftY = top / CELL_SIZE;
    this.landmark.width = scaleX * width / CELL_SIZE;
    this.landmark.height = scaleY * height / CELL_SIZE;
  }

  setLabel(label) {
    this.label.set({ text: label });
    this.canvas.requestRenderAll();
    this.canvas.fire('object:modified');
  }

  renderLabel(label = '') {
    return new fabric.Text(label, {
      fontSize: FONT_SIZE,
      fontFamily: FONT_FAMILY,
      fontWeight: FONT_WEIGHT,
      selectable: false,
      originX: 'center',
      originY: 'center',
      angle: -this.landmark.rotation
    });
  }

  setActive() {
    if (!this.renderedElement) return;
    if (this.canvas.getActiveObject().type === 'activeSelection') return;
    this.canvas.setActiveObject(this.renderedElement);
    this.canvas.requestRenderAll();
  }

  remove() {
    this.canvas.remove(this.renderedElement);
  }
}
